import React, { useState } from 'react';
import { sendEnquiry } from '../../lib/sendEnquiry';

const configs = {
  contact: {
    subject: (form) => `Bright Study enquiry from ${form.name}`,
    success: 'Thank you. Our team will reach out shortly.',
    fields: [
      { name: 'name', label: 'Name', placeholder: 'Full name', required: true },
      { name: 'email', label: 'Email', type: 'email', placeholder: 'Email ID', required: true },
      { name: 'phone', label: 'Mobile', type: 'tel', placeholder: 'Mobile number', required: true },
      { name: 'school', label: 'School', placeholder: 'School / organisation', required: true },
      { name: 'message', label: 'Message', placeholder: 'How can we help?', textarea: true, required: true },
    ],
  },
  speaker: {
    subject: (form) => `Speaker application: ${form.topic || form.name}`,
    success: 'Thank you. We will write back if there is a session that fits.',
    fields: [
      { name: 'name', label: 'Name', placeholder: 'Full name', required: true },
      { name: 'email', label: 'Email', type: 'email', placeholder: 'Email ID', required: true },
      { name: 'organisation', label: 'Organisation', placeholder: 'School or organisation' },
      { name: 'topic', label: 'Topic', placeholder: 'What would you speak about?', required: true },
      { name: 'message', label: 'Outline', placeholder: 'A short outline of the session', textarea: true, required: true },
    ],
  },
  career: {
    subject: (form) => `Career application from ${form.name}`,
    success: 'Thank you. If there is a fit, we will be in touch.',
    fields: [
      { name: 'name', label: 'Name', placeholder: 'Full name', required: true },
      { name: 'email', label: 'Email', type: 'email', placeholder: 'Email ID', required: true },
      { name: 'phone', label: 'Mobile', type: 'tel', placeholder: 'Mobile number' },
      { name: 'role', label: 'Role interest', placeholder: 'Product, success, engineering…', required: true },
      { name: 'message', label: 'Note', placeholder: 'A short note about your work', textarea: true, required: true },
    ],
  },
  newsletter: {
    subject: (form) => `Newsletter signup from ${form.email}`,
    success: 'You are on the list. We will keep the briefing short.',
    fields: [
      { name: 'name', label: 'Name', placeholder: 'Full name' },
      { name: 'email', label: 'Email', type: 'email', placeholder: 'Work email', required: true },
      { name: 'role', label: 'Role', placeholder: 'Principal, coordinator, admin…' },
    ],
  },
};

function InnerEnquiryForm({ type = 'contact' }) {
  const config = configs[type] || configs.contact;
  const initial = config.fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), { website: '' });
  const [form, setForm] = useState(initial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (form.website) return;

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    const payload = {};
    config.fields.forEach((field) => {
      payload[field.label.toLowerCase()] = form[field.name];
    });
    if (form.email) payload.email = form.email;

    try {
      await sendEnquiry(payload, config.subject(form));
      setStatus({ type: 'success', message: config.success });
      setForm(initial);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="inner-form" onSubmit={onSubmit}>
      <div className={`inner-form-grid${config.fields.length <= 3 ? ' is-narrow' : ''}`}>
        {config.fields.map((field) => (
          <label key={field.name} className={field.textarea ? 'is-wide' : ''}>
            {field.label}
            {field.textarea ? (
              <textarea
                name={field.name}
                value={form[field.name]}
                onChange={onChange}
                placeholder={field.placeholder}
                rows="4"
                required={field.required}
              />
            ) : (
              <input
                name={field.name}
                type={field.type || 'text'}
                value={form[field.name]}
                onChange={onChange}
                placeholder={field.placeholder}
                required={field.required}
              />
            )}
          </label>
        ))}
      </div>
      <input
        className="inner-honey"
        name="website"
        value={form.website}
        onChange={onChange}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      {status.message ? (
        <p className={`inner-status is-${status.type}`}>{status.message}</p>
      ) : null}
      <button type="submit" className="inner-btn inner-btn-primary" disabled={isSubmitting}>
        {isSubmitting ? 'Sending…' : 'Submit'}
      </button>
    </form>
  );
}

export default InnerEnquiryForm;
